import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, message, Trans } from '@/components'
import { Modal } from '@/components'
import { TeamRole } from '@/consts/team'

import { AddMemberForm, NewMemberResult } from '../types/types'
import { AddMemberFormView, AddMemberFormViewHandle } from './AddMemberForm'
import { AddMemberResultView } from './AddMemberResult'

type Step = 'form' | 'result'

export function AddMemberDialog({
  open,
  onClose,
  adminBalance,
  defaultRole = TeamRole.Normal,
  createMember,
  onCreated,
  teamDomain,
}: {
  open: boolean
  onClose: () => void
  adminBalance: number
  defaultRole?: TeamRole
  createMember: (payload: AddMemberForm) => Promise<NewMemberResult>
  onCreated?: (result: NewMemberResult, form: AddMemberForm) => void
  teamDomain: string
}) {
  const { t } = useTranslation()
  const formRef = useRef<AddMemberFormViewHandle>(null)

  const [step, setStep] = useState<Step>('form')
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<NewMemberResult | null>(null)
  const [_lastForm, setLastForm] = useState<AddMemberForm | null>(null)
  const [loading, setLoading] = useState(false)

  const [canSubmit, setCanSubmit] = useState(false)

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep('form')
        setSubmitting(false)
        setResult(null)
        setLastForm(null)
        setCanSubmit(false)
      }, 150)
    }
  }, [open])

  const handleSubmit = async (payload: AddMemberForm) => {
    if (submitting) return
    setSubmitting(true)
    setLoading(true)
    try {
      const res = await createMember(payload)
      setResult(res)
      setLastForm(payload)
      onCreated?.(res, payload)
      setStep('result')
      message.success(t('team.member.createdDialog.memberCreatedSuccess'))
    } catch (e: any) {
      console.warn('Create member failed:', e)
      // message.error(e?.message || 'Failed to create member. Please try again.')
    } finally {
      setSubmitting(false)
      setLoading(false)
    }
  }

  const getRoleLabel = (role: TeamRole): string => {
    if (role === TeamRole.Owner) return t('team.role.owner')
    if (role === TeamRole.Admin) return t('team.role.admin')
    return t('team.role.member')
  }

  const copyAll = async () => {
    if (!result) return
    const text = t('team.member.createdDialog.copyAllTemplate', {
      id: result.id,
      account: result.account,
      role: getRoleLabel(result.role),
      credits: result.credits,
      password: result.password,
    })
    await navigator.clipboard.writeText(text)
    message.success(t('team.member.createdDialog.copySuccess'))
  }

  return (
    <Modal open={open} onClose={onClose} className="w-[640px]">
      <Modal.Header>
        {step === 'form' ? (
          <Trans i18nKey="team.member.addMembers">Add Members</Trans>
        ) : (
          <Trans i18nKey="team.member.newMemberCreated">New Member Created</Trans>
        )}
      </Modal.Header>

      <Modal.Body className="max-h-[70vh] overflow-y-auto" scrollable>
        {step === 'form' ? (
          <AddMemberFormView
            ref={formRef}
            balance={adminBalance}
            defaultRole={defaultRole}
            onCancel={onClose}
            onSubmit={handleSubmit}
            teamDomain={teamDomain}
            loading={loading}
            onValidityChange={setCanSubmit}
          />
        ) : result ? (
          <AddMemberResultView data={result} onClose={onClose} onCopyAll={copyAll} />
        ) : null}
      </Modal.Body>

      {step === 'form' && (
        <Modal.Footer>
          <div className="flex items-center justify-end gap-4 w-full">
            <Button type="outline" className="min-w-[96px]" onClick={onClose}>
              <Trans i18nKey="common.cancel">Cancel</Trans>
            </Button>
            <Button
              type="secondary"
              className="min-w-[140px]"
              disabled={!canSubmit || loading}
              onClick={() => formRef.current?.triggerSubmit()}
            >
              <Trans i18nKey="team.member.addMember">Add Member</Trans>
            </Button>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  )
}
